import { IDataStore } from "csi/typings";
import { action, computed, makeObservable, observable, runInAction } from "mobx";

class DataStoreLoader<T> {
	@observable private _columns = 0;
	@observable private _error: Error;
	@observable private _loading = false;
	@observable private _numberLoaded = 0;
	@observable private _promise: Promise<number>;
	@observable private _total: number;

	/** data store */
	private _store: IDataStore<T>;

	/** offset to calculation for number of results to load for first page to complete the rows (such as when special items are placed at beginning) */
	firstPageOffset = 0;

	/** incremental number of rows to load and render at a time */
	rowsPerPage: number;

	/** error encountered while loading results */
	@computed get error(): Error {
		return this._error;
	}

	/** indicate if there are more results */
	@computed get hasMoreResults(): boolean {
		const { _loading, _numberLoaded, _total } = this;
		return !_loading && (_numberLoaded < _total);
	}

	/** indicate if there's any result after loading, for example, user could delete the data */
	@computed get hasNoResultsAfterLoading(): boolean {
		const { _loading, _store, results } = this;
		return _store && !_loading && results.length === 0;
	}

	/** indicate if store has results, which is assumed true if not yet loaded */
	@computed get hasResults(): boolean {
		const { _loading, _numberLoaded, _promise } = this;
		return !_promise || _loading || !!_numberLoaded;
	}

	/** number of items displayed in grid, which includes "new" item if there is one */
	@computed get numberDisplayed(): number {
		const { firstPageOffset, _numberLoaded } = this;
		return _numberLoaded ? (_numberLoaded + firstPageOffset) : 0;
	}

	/** number of skeletons to render */
	@computed get numberOfSkeletons(): number {
		// ensure that we render enough skeletons to occupy one more row
		const { numberDisplayed, _columns, _loading, _total } = this;

		if (_loading) {
			const rows = Math.ceil(numberDisplayed / _columns) + 1;
			const total = _total || _columns;
			const target = Math.min(rows * _columns, total);
			return target - numberDisplayed;
		}

		return 0;
	}

	/** array of results to render */
	@computed get results(): T[] {
		const { _numberLoaded, _store } = this;
		const results = [];

		// the store might remove some items
		const counts = Math.min(_numberLoaded, _store?.total);

		for (let i = 0; i < counts; ++i) {
			results[i] = _store.get(i);
		}

		return results.filter((item) => item != null);
	}

	/** array of result and identity pairs */
	@computed get resultsWithIds(): [T, string][] {
		const { results, _store } = this;

		if (!_store) {
			return [];
		}

		const { getIdentity } = _store;
		const identityFunction = getIdentity ?
			(item: T, index: number): string => getIdentity(item) :
			(item: T, index: number): string => String(index);

		return results.map((item, i) => [item, identityFunction(item, i)]);
	}

	/** return promise resolving to total number of results */
	getTotalPromise(): Promise<number> {
		if (!this._promise) {
			this.loadMoreResults(1);
		}

		return this._promise;
	}

	/** load next page of results */
	@action loadMoreResults = async (targetNumberOfResults?: number): Promise<void> => {
		const { firstPageOffset, numberDisplayed, rowsPerPage, _columns, _error, _numberLoaded, _store } = this;

		if (_error || !_columns || !_store) {
			return;
		}

		if (!targetNumberOfResults) {
			// determine number of results to load for next page
			const rows = Math.ceil(numberDisplayed / _columns) + Math.max(rowsPerPage, 1);
			targetNumberOfResults = (rows * _columns) - firstPageOffset;
		}

		// if number of results is already loaded, then return
		if (targetNumberOfResults <= _numberLoaded) {
			return;
		}

		// load the next page
		this._loading = true;

		try {
			this._promise = _store.load(_numberLoaded, targetNumberOfResults - 1);

			const total = await this._promise;

			runInAction(() => {
				// before updating properties, ensure that store has not been changed in which case these results no longer matter
				if (_store === this._store) {
					this._loading = false;
					this._numberLoaded = Math.min(total, targetNumberOfResults);
					this._total = total;
				}
			});
		} catch (err) {
			runInAction(() => {
				// error encountered
				if (_store === this._store) {
					this._error = err;
					this._loading = false;
					this._promise = undefined;
				}
			});
		}
	};

	/** retry loading more results once an error was encountered */
	@action retry = (): void => {
		if (this._error) {
			this._error = null;
			this.loadMoreResults();
		}
	};

	/** track number of columns, and if no results yet, then start loading */
	@action setColumns = (columns: number): void => {
		if (columns !== this._columns) {
			this._columns = columns;

			if (this._total === undefined) {
				this.loadMoreResults();
			}
		}
	};

	/** set data store */
	@action setStore = (store: IDataStore<T>): void => {
		if (store !== this._store) {
			this._error = undefined;
			this._loading = false;
			this._numberLoaded = 0;
			this._promise = undefined;
			this._store = store;
			this._total = undefined;
			this.loadMoreResults();
		}
	};

	constructor(store?: IDataStore<T>, columns = 0, rowsPerPage = 5) {
		makeObservable(this);
		this.rowsPerPage = rowsPerPage;
		this._columns = columns;
		this.setStore(store);
	}
}

export default DataStoreLoader;
